<template>
  <v-card class="py-2">
    <v-icon v-on:click.stop="$emit('close')" large class="closeIcon mt-3 mx-2"
      >mdi-close</v-icon
    >
    <h2
      weight="200"
      class="mt-6 d-flex font-weight-regular justify-center align-center"
    >
      Modifier le dossier "{{
        nameOrigin ? truncateString(nameOrigin) : "..."
      }}"
    </h2>
    <v-divider light color="#EF7D00" class="mt-4 mb-6 mx-4"></v-divider>
    <v-form
      ref="form"
      v-model="isFormValid"
      class="d-flex flex-column px-10 pt-4"
    >
      <v-row>
        <v-text-field
          v-model="directory.name"
          label="Nom du dossier"
          prepend-icon="mdi-file-document"
          required
          :rules="name_rule"
        ></v-text-field>
      </v-row>
      <v-row class="d-flex align-center">
        <v-col>Visibilté : </v-col>
      </v-row>
      <div v-for="field in visibilityFields" :key="field.key" class="ml-4 mb-2">
        <v-autocomplete
          v-model="directory.visibility[field.key]"
          :items="field.items"
          :label="field.label"
          :prepend-icon="field.icon"
          :rules="field.rules"
          multiple
          dense
        >
          <template v-slot:selection="{ item }">
            <v-chip
              close
              class="mx-1 my-1"
              @click:close="removeVisibilityItem(field.key, item)"
            >
              {{ item }}
            </v-chip>
          </template>
        </v-autocomplete>
      </div>
      <v-row class="d-flex align-center">
        <v-col
          >Déplacer
          <span v-if="newParent"
            >dans le dossier {{ newParent.name }}</span
          ></v-col
        >
      </v-row>
      <TreeForMoving :itemId="directory._id" @change="changeNewParent" />
    </v-form>
    <div class="d-flex justify-center mb-4 mt-2">
      <v-btn @click="deleteDirectory()" large elevation="2" color="error">
        <v-icon>mdi-delete</v-icon> Supprimer le dossier
      </v-btn>
    </div>
    <ValidateErrorButtons
      @successClicked="validate()"
      @errorClicked="$emit('close')"
      :disabled="!isFormValid"
    >
      <template v-slot:error>ANULER</template>
      <template v-slot:success>VALIDER</template>
    </ValidateErrorButtons>
  </v-card>
</template>

<script>
import ValidateErrorButtons from "../../components/ValidateErrorButtons.vue";
import TreeForMoving from "./TreeForMoving.vue";

const API_TO_UI_ROLES = { teacher: "Tuteur", student: "Tutoré" };
const UI_TO_API_ROLES = { Tuteur: "teacher", Tutoré: "student" };

export default {
  name: "DialogModifyDirectory",
  components: {
    ValidateErrorButtons,
    TreeForMoving,
  },
  props: {
    directory: Object,
    parent: Object,
  },
  data: () => ({
    isFormValid: false,

    nameOrigin: null,
    newParent: null,
  }),
  computed: {
    name_rule: function () {
      return [
        !!this.directory.name || "Le nom ne peut pas être vide",
        !this.parent.children.directories
          .filter((i) => i._id !== this.directory._id)
          .map((i) => i.name.toLowerCase())
          .includes(this.directory.name.toLowerCase()) ||
          "ce nom est déjà utilisé par un autre dossier",
      ];
    },
    visibilityFields() {
      return [
        {
          key: "roles",
          label: "Rôles",
          icon: "mdi-book",
          items: ["Tuteur", "Tutoré"],
          rules: this.createVisibilityRules("roles", "rôle"),
        },
        {
          key: "schools",
          label: "Écoles",
          icon: "mdi-school",
          items: ["ESILV", "EMLV", "IIM"],
          rules: this.createVisibilityRules("schools", "école"),
        },
        {
          key: "years",
          label: "Années",
          icon: "mdi-cake",
          items: ["A1", "A2", "A3", "A4", "A5"],
          rules: this.createVisibilityRules("years", "année"),
        },
      ];
    },
  },
  methods: {
    changeNewParent(item) {
      this.newParent = item;
    },
    truncateString(str, maxLength = 27) {
      if (str.length <= maxLength) return str;
      return str.slice(0, maxLength - 1) + "…";
    },
    validate: function () {
      let payload = {
        name: this.directory.name,
        parent: this.newParent ? this.newParent._id : this.directory.parent,
        visibility: {
          roles: this.directory.visibility.roles.map((r) => UI_TO_API_ROLES[r]),
          schools: this.directory.visibility.schools,
          years: this.directory.visibility.years,
        },
      };
      this.$store
        .dispatch("leodata/modifyDirectory", {
          directoryId: this.directory._id,
          payload,
        })
        .then(() => {
          this.$emit("close");
        });
    },
    createVisibilityRules(key, singularName) {
      if (!this.parent?.visibility) return [true];

      let parentVisibilityItems = [];
      if (key === "roles") {
        parentVisibilityItems =
          this.parent.visibility.roles?.map((r) => API_TO_UI_ROLES[r] || r) ||
          [];
      } else {
        parentVisibilityItems = this.parent.visibility[key] || [];
      }

      return [
        (v) => {
          const errors = (v || []).filter(
            (item) => !parentVisibilityItems.includes(item)
          );
          if (errors.length === 0) return true;
          const plural = errors.length > 1;
          return `Le${plural ? "s" : ""} ${singularName}${
            plural ? "s" : ""
          } "${errors.join(", ")}" n'est pas permis par le dossier parent.`;
        },
      ];
    },
    removeVisibilityItem(key, item) {
      const index = this.directory.visibility[key].indexOf(item);
      if (index >= 0) this.directory.visibility[key].splice(index, 1);
    },
    initializeForm() {
      this.nameOrigin = this.directory.name;
      this.directory.visibility.roles =
        this.directory.visibility.roles.map((r) => API_TO_UI_ROLES[r]) || [];
      this.newParent = null;
    },
    deleteDirectory() {
      this.$swal
        .fire({
          title: `Êtes-vous sûr de vouloir supprimer ce dossier ?`,
          icon: "warning",
          html: `Supprimer ce dossier entraînera la suppresion de tout ces dossiers et fichiers enfants. Cette action sera irréversible.`,
          confirmButtonText: "Supprimer",
          confirmButtonColor: "#d33",
          showCancelButton: true,
          cancelButtonText: "Annuler",
        })
        .then((result) => {
          if (result.isConfirmed) {
            this.$store.dispatch("leodata/deleteDirectory", this.directory._id);
            this.$emit("close");
          }
        });
    },
  },
  created() {
    this.initializeForm();
  },
  watch: {
    directory(newValue) {
      if (newValue) {
        this.initializeForm();
      }
    },
  },
};
</script>
<style scoped>
.v-sheet {
  position: relative;
  color: #8f8f8f;
}
.v-icon,
.v-input {
  color: #8f8f8f;
}
.closeIcon {
  position: absolute;
  right: 0;
  top: 0;
}

.discord_link {
  color: inherit;
}
</style>
